import * as THREE from 'three';
import {SketchTrimMode} from '../SketchTrimTypes';
import type {SketchTrimEntityRef} from '../SketchTrimTypes';
import type {SketchTrimModeService} from './SketchTrimModeServiceTypes';
import type {SketchTrimController} from '../SketchTrimController';

/**
 * Corner 模式服务。
 *
 * 职责：
 * 1. 管理两次顺序选线状态；
 * 2. 消费选择管理器结算结果；
 * 3. 调度角点执行并发布提示。
 */
export class SketchTrimCornerModeService implements SketchTrimModeService {
    /** 当前服务绑定的模式。 */
    public readonly mode = SketchTrimMode.Corner;

    /** 第一条已选开放曲线。 */
    private firstEntity: SketchTrimEntityRef | null = null;
    /** 所属剪裁控制器。 */
    private readonly controller: SketchTrimController;

    /**
     * 构造函数。
     * @param controller 剪裁控制器。
     */
    public constructor(controller: SketchTrimController) {
        this.controller = controller;
    }

    /**
     * 获取模式显示文案。
     * @returns 文案。
     */
    public getDisplayName(): string {
        return '角点（Corner）';
    }

    /**
     * 重置模式私有状态。
     * @param context 服务上下文。
     */
    public resetState(): void {
        this.firstEntity = null;
        this.controller.serviceSetLastSelectedEntity(null);
    }

    /**
     * 处理选择管理器结算。
     * @param context 服务上下文。
     */
    public handleSelectionUpdated(): void {
        const selectionManager = this.controller.getViewer().entitySelectionManager;
        if (!selectionManager) {
            return;
        }

        const selectedCurvePicks = selectionManager.getSelectedPicks()
            .filter((pick) => this.controller.serviceIsCornerSelectableCurve(pick.object));
        if (selectedCurvePicks.length <= 0) {
            this.firstEntity = null;
            this.controller.serviceSetLastSelectedEntity(null);
            return;
        }

        const firstEntity = this.controller.serviceCreateTrimEntityRefFromIntersection(
            selectedCurvePicks[0] as unknown as THREE.Intersection,
        );
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(firstEntity));
        if (selectedCurvePicks.length === 1) {
            const isSameFirstEntity = this.firstEntity
                ? this.controller.serviceIsSameTrimEntity(this.firstEntity, firstEntity)
                : false;
            this.firstEntity = firstEntity;
            if (!isSameFirstEntity) {
                this.controller.getViewer().drawingNoticeCallback?.('已选择第一条开放实体，请继续选择第二条开放实体。', 'info');
            }
            return;
        }

        const secondEntity = this.controller.serviceCreateTrimEntityRefFromIntersection(
            selectedCurvePicks[1] as unknown as THREE.Intersection,
        );
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(secondEntity));
        const result = this.controller.serviceApplyCornerOperation(firstEntity, secondEntity);
        this.firstEntity = null;
        this.controller.serviceClearTrimSelectionFromSelectionManager();
        this.controller.servicePublishResult(result);
    }
}
